import { jsxs, jsx } from 'react/jsx-runtime';
import { useId, useState } from 'react';
import { useCombobox } from 'downshift';
import { Input } from '../Input/Input.js';
import { LoadingSpinner } from '../LoadingSpinner/LoadingSpinner.js';
import { CircleInfoIcon } from '../../../icons/CircleInfoIcon.js';
import { CircleExclamationIcon } from '../../../icons/CircleExclamationIcon.js';
import { CircleTickIcon } from '../../../icons/CircleTickIcon.js';

const ChevronIcon = ({ isOpen }) => (jsx("svg", { width: "16", height: "16", viewBox: "0 0 16 16", fill: "none", xmlns: "http://www.w3.org/2000/svg", className: `transition-transform duration-200 ${isOpen ? 'rotate-180' : 'rotate-0'}`, children: jsx("path", { d: "M4 6L8 10L12 6", stroke: "var(--color-neutral-900)", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round" }) }));
const MagnifyingGlassIcon = () => (jsx("svg", { width: "17", height: "17", viewBox: "0 0 17 17", fill: "none", xmlns: "http://www.w3.org/2000/svg", children: jsx("path", { d: "M12 6.5C12 4.5625 10.9375 2.75 9.25 1.75C7.53125 0.78125 5.4375 0.78125 3.75 1.75C2.03125 2.75 1 4.5625 1 6.5C1 8.46875 2.03125 10.2812 3.75 11.2812C5.4375 12.25 7.53125 12.25 9.25 11.2812C10.9375 10.2812 12 8.46875 12 6.5ZM10.7188 11.4375C9.59375 12.4375 8.09375 13 6.5 13C2.90625 13 0 10.0938 0 6.5C0 2.9375 2.90625 0 6.5 0C10.0625 0 13 2.9375 13 6.5C13 8.125 12.4062 9.59375 11.4375 10.75L15.8438 15.1562C16.0312 15.3438 16.0312 15.6875 15.8438 15.875C15.6562 16.0625 15.3125 16.0625 15.125 15.875L10.7188 11.4375Z", fill: "currentColor" }) }));
const styles = {
    container: 'relative w-full',
    label: 'mb-space-200 text-body-strong block',
    menuWrapper: 'mt-space-50 absolute z-10 rounded-radius-100 bg-neutral-0 w-full',
    menu: 'text-body max-h-65 overflow-auto rounded-radius-100 shadow-[0_6px_16px_rgba(0,0,0,0.12)]',
    menuItem: 'min-h-10 px-space-400 py-space-200 cursor-pointer transition-colors',
    menuItemHighlighted: 'bg-primary-100',
    noResults: 'px-space-400 py-space-300 text-body text-center',
    hintTextContainer: 'mt-space-200 flex gap-space-100',
    hintText: 'text-body-small',
    errorText: 'text-body-small text-text-error',
    successText: 'text-body-small text-text-success',
    hintIconContainer: 'flex items-center h-(--line-height-body-small)',
};
const ComboBox = ({ value, defaultValue, options, ref, onInputChange, onSelect, label, noResultsText, hintText, hasError, hasSuccess, asyncProps, disabled, placeholder, hideLabel, required, id, name, allowCustomValue = false, autoComplete, }) => {
    const generatedId = useId();
    const inputId = id ?? generatedId;
    const labelId = `${inputId}-label`;
    const menuId = `${inputId}-menu`;
    const [inputValue, setInputValue] = useState(value || defaultValue || '');
    const defaultOption = options?.find((o) => o.value === defaultValue) || null;
    const initialSelectedOption = options?.find((o) => o.value === value) || defaultOption;
    const filteredOptions = (asyncProps
        ? options
        : options?.filter((option) => {
            if (inputValue === defaultValue)
                return true;
            return option.label.toLowerCase().includes(inputValue.trim().toLowerCase());
        })) || [];
    const { isOpen, getLabelProps, getMenuProps, getInputProps, highlightedIndex, getItemProps, selectItem, } = useCombobox({
        inputId,
        labelId,
        menuId,
        onInputValueChange({ inputValue: newInputValue, type }) {
            if (type === useCombobox.stateChangeTypes.InputChange) {
                onInputChange?.(newInputValue || '');
                setInputValue(newInputValue || '');
            }
        },
        items: filteredOptions,
        itemToString: (item) => (item ? item.label : ''),
        onSelectedItemChange: ({ selectedItem }) => {
            onSelect?.(selectedItem?.value || null);
        },
        initialSelectedItem: initialSelectedOption,
        initialInputValue: value || defaultValue || '',
        stateReducer: (state, actionAndChanges) => {
            const { type, changes } = actionAndChanges;
            switch (type) {
                case useCombobox.stateChangeTypes.InputChange: {
                    if (!allowCustomValue &&
                        state.selectedItem &&
                        changes.inputValue !== state.selectedItem.label) {
                        return {
                            ...changes,
                            selectedItem: null,
                        };
                    }
                    const match = options?.find((option) => option.label === changes.inputValue);
                    if (match) {
                        return {
                            ...changes,
                            selectedItem: match,
                        };
                    }
                    return changes;
                }
                default:
                    return changes;
            }
        },
    });
    const getRightIcon = () => {
        if (asyncProps) {
            if (asyncProps.loading) {
                return jsx(LoadingSpinner, {});
            }
            return (jsx("span", { className: `pointer-events-none ${hasSuccess ? 'text-input-border-success' : 'text-body'}`, children: jsx(MagnifyingGlassIcon, {}) }));
        }
        return jsx(ChevronIcon, { isOpen: isOpen });
    };
    return (jsxs("div", { className: styles.container, children: [jsxs("div", { className: "relative", children: [label && (jsx("label", { ...getLabelProps(), htmlFor: inputId, className: `${styles.label} ${hideLabel ? 'sr-only' : ''}`, children: label })), jsx(Input, { ...getInputProps({
                            ref: ref,
                            onKeyDown: (e) => {
                                if (e.key === 'Enter') {
                                    if (filteredOptions.length === 1) {
                                        const match = filteredOptions[0];
                                        setInputValue(match.label);
                                        selectItem(match);
                                    }
                                }
                            },
                            disabled,
                            placeholder,
                            required,
                            id: inputId,
                            name,
                            autoComplete,
                        }), rightIcon: getRightIcon(), hasSuccess: !!hasSuccess, hasError: !!hasError }), jsx("div", { className: styles.menuWrapper, children: jsx("ul", { className: `${styles.menu} ${!isOpen && 'hidden'} ${!!filteredOptions.length && !asyncProps?.loading && 'p-2.5'}`, ...getMenuProps(), children: options && inputValue && filteredOptions.length === 0 && noResultsText ? (jsx("li", { className: styles.noResults, children: noResultsText })) : (!asyncProps?.loading &&
                                filteredOptions.map((option, index) => {
                                    const isHighlighted = highlightedIndex === index;
                                    return (jsx("li", { className: `${styles.menuItem} ${isHighlighted && styles.menuItemHighlighted}`, ...getItemProps({ item: option, index }), children: jsxs("div", { className: "flex flex-col", children: [jsx("span", { className: "text-sm font-medium", children: option.label }), option.description && (jsx("span", { className: "text-xs text-neutral-900", children: option.description }))] }) }, option.value));
                                })) }) })] }), hintText &&
                (!hasError || typeof hasError !== 'string') &&
                (!hasSuccess || typeof hasSuccess !== 'string') && (jsxs("div", { className: styles.hintTextContainer, children: [jsx("div", { className: styles.hintIconContainer, children: jsx(CircleInfoIcon, {}) }), jsx("p", { className: styles.hintText, children: hintText })] })), hasError && typeof hasError === 'string' && (jsxs("div", { className: `${styles.hintTextContainer} text-text-error`, children: [jsx("div", { className: styles.hintIconContainer, children: jsx(CircleExclamationIcon, {}) }), jsx("p", { className: styles.errorText, role: "alert", children: hasError })] })), !hasError && hasSuccess && typeof hasSuccess === 'string' && (jsxs("div", { className: `${styles.hintTextContainer} text-text-success`, children: [jsx("div", { className: styles.hintIconContainer, children: jsx(CircleTickIcon, {}) }), jsx("p", { className: styles.successText, children: hasSuccess })] }))] }));
};

export { ComboBox };
